import { clsx } from 'clsx';
import { useCallback, useEffect, useState } from 'react';

/* -------------------------------------------------------------------------
   Theme

   Three states, not two: light, dark, and "follow the system". Defaulting to a
   fixed theme overrides a preference the operator already expressed to their OS,
   and an operator on shift at 2am has usually expressed it deliberately.

   The chosen theme is written to `data-vc-theme` on <html>, which is what the
   token file keys off.
   ------------------------------------------------------------------------- */

export const THEME_STORAGE_KEY = 'vc.theme';
export type ThemePreference = 'light' | 'dark' | 'system';

function resolve(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function apply(preference: ThemePreference): void {
  document.documentElement.dataset.vcTheme = resolve(preference);
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // Storage can throw in a locked-down browser profile. A theme preference is
    // never worth failing to render the console over.
    return 'system';
  }
}

/**
 * Applies the stored preference immediately, before React renders.
 *
 * `useTheme` applies it from an effect, which runs after the first paint and
 * only where the hook is mounted — and it was mounted only inside the console
 * shell. Every page outside it (sign-in, the OAuth callback, the loading
 * curtain, error states) therefore rendered light no matter what the operator
 * had chosen.
 *
 * Called once at startup, so the attribute is on `<html>` before anything is
 * drawn. It cannot be an inline script in the HTML: the console's CSP is
 * `script-src 'self'` with no `unsafe-inline`, deliberately.
 */
export function applyStoredTheme(): void {
  try {
    apply(readStoredPreference());
  } catch {
    /* a console that cannot read a theme preference still renders */
  }
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStoredPreference);

  useEffect(() => {
    apply(preference);
    try {
      globalThis.localStorage?.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      /* ignore — see readStoredPreference */
    }

    if (preference !== 'system') return;

    // Follow the OS live while set to system.
    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = () => apply('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const cycle = useCallback(() => {
    setPreference((current) =>
      current === 'system' ? 'light' : current === 'light' ? 'dark' : 'system',
    );
  }, []);

  return { preference, setPreference, cycle, resolved: resolve(preference) };
}

const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'Theme: follow system',
  light: 'Theme: light',
  dark: 'Theme: dark',
};

export function ThemeToggle({
  preference,
  onCycle,
  className,
}: {
  preference: ThemePreference;
  onCycle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onCycle}
      aria-label={`${THEME_LABEL[preference]}. Activate to change.`}
      title={THEME_LABEL[preference]}
      className={clsx(
        'flex size-7 items-center justify-center rounded-[var(--vc-radius-md)]',
        'text-[var(--vc-text-muted)] transition-colors duration-[var(--vc-duration-fast)]',
        'hover:bg-[var(--vc-surface-hover)] hover:text-[var(--vc-text)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)]',
        className,
      )}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="size-4">
        {preference === 'light' ? (
          <>
            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
            <path
              d="M12 2.6v2.2M12 19.2v2.2M21.4 12h-2.2M4.8 12H2.6m15.3-6.3-1.6 1.6M7.5 16.5l-1.6 1.6m12 0-1.6-1.6M7.5 7.5 5.9 5.9"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </>
        ) : preference === 'dark' ? (
          <path
            d="M20.5 14.4A8.6 8.6 0 0 1 9.6 3.5a8.6 8.6 0 1 0 10.9 10.9Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <rect
              x="2.8"
              y="4.5"
              width="18.4"
              height="13"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.7"
            />
            <path d="M8.5 21h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </>
        )}
      </svg>
    </button>
  );
}
